/**
 * Local persistence of the company onboarding draft, so that a reload during a
 * ~120s scrape or a long review does not cost the user their work (SPEC AC 23).
 *
 * **Why not `makePersisted`** (as the freelance twin does in
 * `auth/onboarding/context/OnboardingContext.tsx`): `makePersisted` hydrates
 * asynchronously behind an `unchanged` latch — any write landing before its init
 * resolves makes it discard the persisted value outright (see the docblock of
 * `active-search/components/SearchLayout.tsx`). This flow seeds form defaults on
 * first render, so that write is guaranteed: the draft would be silently lost
 * exactly in the case it exists to cover.
 *
 * Hence a plainly asynchronous API on localforage. The contract for consumers is
 * one rule: **await `readDraft()` before seeding anything or writing anything.**
 * Ordering is the caller's, not a latch's.
 *
 * The key is scoped per user (`company_onboarding_draft:<userId>`): on a shared
 * device two people must never see each other's draft. Logout wipes every key
 * (`clearAllCompanyOnboardingDrafts`) — when a token refresh fails there is no
 * user id left to scope by, and privacy beats the resume promise (PLAN D15).
 *
 * Resume *policy* (which phases may be restored, `loading` → `url` degradation,
 * session guards) is deliberately not here: it is the phase machine's semantics.
 */

import localforage from "localforage";
import { tryCatch } from "@/libs/try-catch";
import type {
  CompanyOnboardingDraft,
  CompanyOnboardingFieldKey,
  CompanyOnboardingManualStep,
  CompanyOnboardingPhase,
  CompanyOnboardingSource,
  CompanyOnboardingValues,
} from "../types";
import { COMPANY_ONBOARDING_DRAFT_VERSION } from "../types";

const DRAFT_KEY_PREFIX = "company_onboarding_draft:";

/**
 * What a consumer supplies. `version`, `userId` and `savedAt` are stamped here
 * so no caller can get them wrong.
 */
export type CompanyOnboardingDraftInput = Omit<
  CompanyOnboardingDraft,
  "version" | "userId" | "savedAt"
>;

/** Nullish while the session is still loading — callers must not have to guard. */
type UserId = string | null | undefined;

export const companyOnboardingDraftKey = (userId: string) =>
  `${DRAFT_KEY_PREFIX}${userId}`;

/** No id, no key: an empty one would be a single draft shared by everybody. */
const resolveKey = (userId: UserId): string | null => {
  const trimmed = typeof userId === "string" ? userId.trim() : "";
  return trimmed ? companyOnboardingDraftKey(trimmed) : null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/*
 * The `satisfies` clauses below are the point of these three tables: add or
 * rename anything in `CompanyOnboardingValues` / the phase / source unions and
 * this file stops compiling, instead of silently accepting a stale draft shape.
 */

const DRAFT_SOURCES = {
  onboarding: true,
  creation: true,
} satisfies Record<CompanyOnboardingSource, true>;

const DRAFT_PHASES = {
  url: true,
  loading: true,
  review: true,
  manual: true,
  build: true,
  success: true,
} satisfies Record<CompanyOnboardingPhase, true>;

const DRAFT_VALUE_KEYS = Object.keys({
  companyName: true,
  website: true,
  invoiceEmail: true,
  invoiceAddress: true,
  invoiceCountry: true,
  invoiceNumber: true,
  description: true,
  industry: true,
  size: true,
  headquarters: true,
  foundedYear: true,
  remotePolicy: true,
} satisfies Record<
  CompanyOnboardingFieldKey,
  true
>) as CompanyOnboardingFieldKey[];

const DRAFT_MANUAL_STEPS = {
  nome: true,
  fatturazione: true,
  piva: true,
  racconta: true,
  dettagli: true,
  conferma: true,
} satisfies Record<CompanyOnboardingManualStep, true>;

const isSource = (value: unknown): value is CompanyOnboardingSource =>
  typeof value === "string" && Object.hasOwn(DRAFT_SOURCES, value);

/**
 * Strict like the phase, and for the same reason: the manual flow seeds its
 * starting screen from this, so an unknown step would drop the user back to the
 * first question with their answers still in the store. `null` is legitimate —
 * it is what every non-manual phase stores.
 */
const isManualStep = (
  value: unknown,
): value is CompanyOnboardingManualStep | null =>
  value === null ||
  (typeof value === "string" && Object.hasOwn(DRAFT_MANUAL_STEPS, value));

/**
 * Checked strictly, unlike the free-text fields: the phase machine switches on
 * it, so an unknown phase would mount nothing at all.
 */
const isPhase = (value: unknown): value is CompanyOnboardingPhase =>
  typeof value === "string" && Object.hasOwn(DRAFT_PHASES, value);

/**
 * Every value is a string in this contract, so the check is "all keys present
 * and all strings". The literal unions (`invoiceCountry`, `size`,
 * `remotePolicy`) are not re-checked on purpose: they come from our own selects,
 * and throwing away a whole review over one odd string would cost more than it
 * protects.
 */
const isValues = (value: unknown): value is CompanyOnboardingValues =>
  isRecord(value) &&
  DRAFT_VALUE_KEYS.every((key) => typeof value[key] === "string");

/**
 * Tolerant of absence, unlike every check above it: a draft written before this
 * field existed is a perfectly good draft whose import simply was never refused.
 * Dropping it would cost a real in-progress creation to gain nothing.
 */
const isRateLimitedAt = (value: unknown): value is string | null | undefined =>
  value === null || value === undefined || typeof value === "string";

const isImportOutcome = (value: unknown): boolean =>
  value === null ||
  (isRecord(value) &&
    isRecord(value.data) &&
    Array.isArray(value.importedFields) &&
    Array.isArray(value.fieldsToConfirm) &&
    typeof value.importedCount === "number");

const tryParseJson = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/**
 * Anything not recognised — bad JSON, another version, a shape from an older
 * build, a payload keyed to a different user — is dropped in silence (PLAN D12).
 * Never throws: a corrupt draft must cost the resume, never the flow.
 */
const parseDraft = (
  raw: unknown,
  userId: string,
): CompanyOnboardingDraft | null => {
  if (typeof raw !== "string") return null;

  const parsed = tryParseJson(raw);
  if (!isRecord(parsed)) return null;

  if (parsed.version !== COMPANY_ONBOARDING_DRAFT_VERSION) return null;
  if (parsed.userId !== userId) return null;
  if (!isSource(parsed.source)) return null;
  if (!isPhase(parsed.phase)) return null;
  if (!isValues(parsed.values)) return null;
  if (!isImportOutcome(parsed.importOutcome)) return null;
  if (!isManualStep(parsed.manualStep)) return null;
  if (typeof parsed.linkedInUrl !== "string") return null;
  if (!isRateLimitedAt(parsed.rateLimitedAt)) return null;
  if (typeof parsed.savedAt !== "string") return null;

  // Rebuilt field by field: extra keys of a tampered payload never reach a consumer.
  return {
    version: COMPANY_ONBOARDING_DRAFT_VERSION,
    userId,
    source: parsed.source,
    phase: parsed.phase,
    values: parsed.values,
    importOutcome:
      parsed.importOutcome as CompanyOnboardingDraft["importOutcome"],
    manualStep: parsed.manualStep,
    linkedInUrl: parsed.linkedInUrl,
    rateLimitedAt: parsed.rateLimitedAt ?? null,
    savedAt: parsed.savedAt,
  };
};

/**
 * Reads the draft of one user. **Await this before seeding form defaults or
 * writing anything** — that ordering is the whole reason this module exists.
 * Resolves `null` when there is nothing usable, and never rejects.
 */
export const readDraft = async (
  userId: UserId,
): Promise<CompanyOnboardingDraft | null> => {
  const key = resolveKey(userId);
  if (!key) return null;

  const [raw, error] = await tryCatch(localforage.getItem<string>(key));
  if (error) return null;

  return parseDraft(raw, (userId as string).trim());
};

/**
 * Persists the draft as a JSON string — the same wire format as the rest of the
 * repo's persisted stores, and a guarantee that what comes back survived
 * `stringify`/`parse` (hence arrays rather than `Set`s in the shared types).
 * Failures (no driver, quota) are swallowed: losing the draft must never break
 * the creation the user is in the middle of.
 */
export const writeDraft = async (
  userId: UserId,
  input: CompanyOnboardingDraftInput,
): Promise<void> => {
  const key = resolveKey(userId);
  if (!key) return;

  const draft: CompanyOnboardingDraft = {
    version: COMPANY_ONBOARDING_DRAFT_VERSION,
    userId: (userId as string).trim(),
    source: input.source,
    phase: input.phase,
    values: input.values,
    importOutcome: input.importOutcome,
    manualStep: input.manualStep,
    linkedInUrl: input.linkedInUrl,
    rateLimitedAt: input.rateLimitedAt,
    savedAt: new Date().toISOString(),
  };

  await tryCatch(localforage.setItem(key, JSON.stringify(draft)));
};

/** Called on the 2xx of the creation mutation: the draft has served its purpose. */
export const clearDraft = async (userId: UserId): Promise<void> => {
  const key = resolveKey(userId);
  if (!key) return;

  await tryCatch(localforage.removeItem(key));
};

/**
 * Wipes every user's draft. This is the logout hook (PLAN D15): a hard logout
 * from a failed token refresh has no user id left to scope by, and on a shared
 * device leaving a draft at rest is the wrong trade.
 */
export const clearAllCompanyOnboardingDrafts = async (): Promise<void> => {
  const [keys, error] = await tryCatch(localforage.keys());
  if (error || !keys) return;

  await Promise.all(
    keys
      .filter((key) => key.startsWith(DRAFT_KEY_PREFIX))
      .map((key) => tryCatch(localforage.removeItem(key))),
  );
};
